/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  CellClickedEvent,
  Column,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ValueGetterParams,
} from '@ag-grid-community/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { omit } from 'lodash';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { startWith, switchMap, tap } from 'rxjs/operators';

import { AddCustomSiteDialogComponent } from '@widgets/add-custom-site-dialog/add-custom-site-dialog.component';
import { ImportSiteAmendmentsDialogComponent } from '@widgets/import-site-amendments-dialog/import-site-amendments-dialog.component';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';
import { WorkflowPanelComponent } from '@features/workflow-panel/workflow-panel.component';

import { PatientProtocolService } from '@models/patient-protocol/patient-protocol.service';
import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';
import { SiteModel, SitesStore } from '@models/sites/sites.store';

import {
  AgCellWrapperComponent,
  getWrapperCellOptions,
} from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { AgIconLinkCellComponent } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.component';
import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { CardComponent } from '@shared/components/card/card.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { EventType, PermissionType, WorkflowStep } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationModel } from '@shared/store/organization/organization.store';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Option } from '@shared/types/components.type';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { Utils } from '@shared/utils/utils';

import { AddBulkSitesDialogComponent } from './components/add-bulk-sites-dialog/add-bulk-sites-dialog.component';
import { BulkUploadSiteInvoiceablesModalComponent } from './components/bulk-upload-site-invoiceables-modal/bulk-upload-site-invoiceables-modal.component';
import { SiteGridData } from './types/sites-grid-row.type';

interface FilterConfig {
  id: string;
  name: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [SiteWorkflowDirective],
  imports: [
    WorkflowPanelComponent,
    CardComponent,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    MultiSelectDropdownComponent,
    NgSelectComponent,
    NgOptionTemplateDirective,
    TooltipDirective,
    NgMultiLabelTemplateDirective,
    ButtonGroupComponent,
    ErrorsTooltipComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
    SlicePipe,
    CheckboxComponent,
  ],
  selector: 'aux-sites',
  styles: [
    `
      :host ::ng-deep .ag-header-cell-resize::after {
        width: 0;
      }

      ::ng-deep .sites-table .ag-cell.grid-cell {
        border-top-width: 0 !important;
        border-left-width: 0 !important;
        border-right-width: 0 !important;
        border-bottom-width: 1px !important;
        border-color: var(--aux-gray-dark) !important;
      }

      ::ng-deep .sites-table .ag-header {
        background-color: transparent;
        border: none;
        border-radius: 0 0 0 0;
      }

      ::ng-deep .sites-table .ag-header-container {
        background-color: #f3f6f7;
        border: 1px solid var(--aux-gray-dark) !important;
        border-radius: 0 0 0 0 !important;
        border-left-width: 0 !important;
      }

      ::ng-deep .sites-table .ag-header-row,
      ::ng-deep .sites-table .ag-header-cell {
        overflow: unset;
      }

      ::ng-deep .sites-table .ag-header-cell-text,
      ::ng-deep .sites-table .ag-row {
        font-size: 1rem;
      }

      ::ng-deep .sites-table .ag-header-cell {
        border-left: 1px solid var(--aux-gray-dark) !important;
        text-align: center;
      }

      ::ng-deep .sites-table .ag-header-cell-label {
        justify-content: center;
      }

      ::ng-deep .sites-table .ag-icon-filter {
        width: 0 !important;
      }

      ::ng-deep .sites-table .ag-pinned-left-cols-container .ag-cell-value.ag-cell,
      ::ng-deep .sites-table .ag-row-group-expanded .ag-cell-value.ag-cell,
      ::ng-deep .sites-table .ag-row-group-contracted .ag-cell-value.ag-cell,
      ::ng-deep .sites-table .ag-pinned-left-cols-container .ag-row,
      ::ng-deep .sites-table .ag-row-group-expanded .ag-row,
      ::ng-deep .sites-table .ag-row-group-contracted .ag-row {
        display: flex;
      }
    `,
  ],
  templateUrl: './sites.component.html',
})
export class SitesComponent implements OnInit {
  eventQuery = inject(EventQuery);
  organizationQuery = inject(OrganizationQuery);
  siteCtx = inject(SiteWorkflowDirective);
  sitesQuery = inject(SitesQuery);
  workflowQuery = inject(WorkflowQuery);

  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private fb = inject(UntypedFormBuilder);
  private injector = inject(Injector);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private overlayService = inject(OverlayService);
  private patientProtocolService = inject(PatientProtocolService);
  private router = inject(Router);
  private sitesService = inject(SitesService);
  private sitesStore = inject(SitesStore);

  showRowsWithSiteFlags = signal(false);

  protocolVersionOptions: Option[] = [];
  reloadTable = new BehaviorSubject(false);

  private permissions = this.authService.getPermissions({
    userHasUpdateSiteCostsPermission: PermissionType.PERMISSION_UPDATE_SITE_COSTS,
    userHasUpdateSitesPermission: PermissionType.PERMISSION_UPDATE_SITES,
  });

  private addSite = (): void => {
    const resp = this.overlayService.openPopup({
      modal: AddCustomSiteDialogComponent,
      settings: {
        header: 'Add Trial Site',
        primaryButton: {
          label: 'Add Trial Site',
        },
      },
    });

    resp.afterClosed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadTable.next(true));
  };

  editOptions = computed<AuxButtonGroup[]>(() => {
    return [
      {
        disabled: !this.permissions().userHasUpdateSitesPermission,
        disabledTooltip: MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
        name: 'Add Site',
        onClick: this.addSite,
      },
      {
        disabled: !this.permissions().userHasUpdateSitesPermission,
        disabledTooltip: MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
        name: 'Add Multiple Sites',
        onClick: () => {
          this.overlayService.openPopup<unknown, unknown, AddBulkSitesDialogComponent>({
            content: AddBulkSitesDialogComponent,
            settings: {
              header: 'Upload Sites',
              panelClasses: ['min-w-[569px]'],
              primaryButton: {
                action: (instance) => instance?.onBulkUpload(),
                label: 'Upload',
              },
            },
          });
          this.eventService
            .select$(EventType.REFRESH_SITES)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.reloadTable.next(true));
        },
      },
      {
        disabled: !this.permissions().userHasUpdateSiteCostsPermission,
        disabledTooltip: MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
        name: 'Upload Bulk Amendments',
        onClick: () => {
          this.overlayService.openPopup<
            {
              protocolVersionOptions: Option<string>[];
            },
            unknown,
            ImportSiteAmendmentsDialogComponent
          >({
            content: ImportSiteAmendmentsDialogComponent,
            data: {
              protocolVersionOptions: this.protocolVersionOptions,
            },
            settings: {
              header: 'Upload Bulk Amendments',
              panelClasses: ['min-w-[569px]'],
              primaryButton: {
                action: (instance) => instance?.onUpload(),
                label: 'Upload',
              },
            },
          });
        },
      },
      this.getBulkUploadOption(),
    ];
  });

  _syncShowRowsWithSiteFlags = effect(() => {
    this.showRowsWithSiteFlags();

    if (this.gridAPI) {
      this.gridAPI.onFilterChanged();
    }
  });
  activatedSitesTotal = 0;
  addButtonGroupOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];
  closedSitesTotal = 0;
  countryOptions$ = new BehaviorSubject<FilterConfig[]>([]);
  currencyOptions$ = new BehaviorSubject<FilterConfig[]>([]);
  currentlyActiveSitesTotal = 0;
  dataLoading$ = new BehaviorSubject(false);
  gridAPI?: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$ = new BehaviorSubject<SiteGridData[]>([]);
  gridOptions$ = new BehaviorSubject<GridOptions<SiteGridData>>({
    columnDefs: [
      {
        cellClass: 'grid-cell justify-left',
        field: 'site_number',
        headerName: 'Site #',
        sort: 'asc',
        sortable: true,
        width: 90,
        ...getWrapperCellOptions(),
      },
      {
        cellClass: 'grid-cell underline aux-link cursor-pointer',
        cellRenderer: AgIconLinkCellComponent,
        cellRendererParams: (params: ICellRendererParams<SiteGridData>) => {
          const flags = params.data?.siteFlags || [];
          const indicatorClass = flags.length > 1 ? 'text-aux-red-dark' : 'text-orange-400';

          return {
            icons: [
              {
                icon: 'AlertTriangle',
                iconClass: indicatorClass,
                iconTooltipMessages: flags,
                showIcon: !!flags.length,
              },
            ],
            iconTooltipMessages: flags,
            rowReverse: true,
          };
        },
        field: 'name',
        headerName: 'Site Name',
        onCellClicked: (event) => this.navigateToSiteInfoPage(event),
        sortable: true,
        tooltipField: 'name',
        width: 240,
      },
      {
        cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
        field: 'investigator_name',
        headerName: 'Investigator',
        sortable: true,
        tooltipField: 'investigator_name',
        valueFormatter: (params) => {
          return params.value ? params.value : Utils.zeroHyphen;
        },
        width: 150,
        ...getWrapperCellOptions(),
      },
      {
        cellClass: 'grid-cell justify-left ',
        cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
        colId: 'country',
        field: 'country',
        filter: true,
        filterValueGetter: (params: ValueGetterParams) =>
          params.data.country ? Utils.countries[params.data.country.slice(8)] : Utils.zeroHyphen,
        headerName: 'Country',
        sortable: true,
        tooltipValueGetter: (params) => {
          return params.valueFormatted !== Utils.zeroHyphen ? params.valueFormatted : '';
        },
        valueFormatter: (params) => {
          return params.value ? Utils.countries[params.value.slice(8)] : Utils.zeroHyphen;
        },
        width: 150,
        ...getWrapperCellOptions(),
      },
      {
        colId: 'currency',
        field: 'currency',
        filter: true,
        headerName: 'Currency',
        sortable: true,
        width: 90,
      },
      {
        cellClass: 'grid-cell justify-end',
        field: 'target_patients',
        headerName: 'Target Patients',
        sortable: true,
        valueFormatter: (params) => {
          return params.value ? params.value : Utils.zeroHyphen;
        },
        width: 90,
      },
      {
        field: 'managed_by',
        headerName: 'Managed by',
        sortable: true,
        ...getWrapperCellOptions(),
        cellClass(params: CellClassParams) {
          if (params.value !== Utils.zeroHyphen) {
            return 'grid-cell underline aux-link cursor-pointer ';
          }

          return 'grid-cell';
        },
        cellRenderer: Utils.getCellWrapper('ag-cell-value', 'value'),
        onCellClicked: (event) => {
          if (event.value !== Utils.zeroHyphen) {
            this.navigateToVendorPage(event);
          }
        },
        tooltipValueGetter: (params) => {
          return params.value !== Utils.zeroHyphen ? params.value : '';
        },
        valueGetter: (params) => {
          return this.organizationQuery.getEntity(params.data.managed_by)?.name
            ? this.organizationQuery.getEntity(params.data.managed_by)?.name
            : Utils.zeroHyphen;
        },
        width: 150,
      },
      {
        field: 'site_activation',
        headerName: 'Site Activation Date',
        sortable: true,
        valueFormatter: (params) => params.value || Utils.zeroHyphen,
        width: 140,
      },
      {
        field: 'closeout_date',
        headerName: 'Site Close Out Date',
        sortable: true,
        valueFormatter: (params) => params.value || Utils.zeroHyphen,
        width: 140,
      },
      {
        field: 'managed_by_name',
        filter: true,
        hide: true,
      },
    ],
    defaultColDef: {
      ...omit(TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF, 'resizable'),
      cellClass: 'grid-cell',
      cellRenderer: AgCellWrapperComponent,
      headerClass: ['bg-aux-dark-light', 'justify-center'],
      minWidth: 40,
      resizable: true,
      sortable: false,
    },
    doesExternalFilterPass: (node) => {
      const flags = node.data.siteFlags || [];

      return this.showRowsWithSiteFlags() ? !!flags.length : true;
    },
    isExternalFilterPresent: () => true,
    rowHeight: 50,
    suppressHorizontalScroll: false,
  } as GridOptions);
  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  initialFilters = {
    country: null,
    currency: null,
    managed_by_name: null,
    name_filter: null,
  };
  isClosingPanelEnabled = this.launchDarklyService.$select(
    (flags) => flags.closing_checklist_toolbar
  );
  isProtocolVersionLoading$ = new BehaviorSubject(true);
  isQuarterCloseEnabled = this.workflowQuery.isWorkflowAvailable;
  loadingTable = new BehaviorSubject(false);
  optionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -10,
      offsetY: 8,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  protocolForm!: FormGroup;
  showTotalCardsSection$ = this.launchDarklyService.select$((flags) => flags.site_analytic_cards);
  transactionsForm = this.fb.group(this.initialFilters);
  userHasLockInvestigatorDataPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_CHECKLIST_PATIENT_DATA],
  });
  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER;
  readonly zeroHyphen = Utils.zeroHyphen;

  ngOnInit(): void {
    this.reloadTable
      .pipe(
        startWith(null),
        tap(() => this.sitesStore.setLoading(true)),
        switchMap(() => this.organizationService.get()),
        switchMap(() => this.sitesService.getForSitesPage()),
        tap(() => this.sitesStore.setLoading(false))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadingTable.next(true);
        this.gridDataReady();
        this.loadingTable.next(false);
      });

    this.transactionsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((x) => {
      const countryInstance = this.gridAPI?.getFilterInstance('country');

      if (countryInstance) {
        countryInstance.setModel({ values: x?.country?.length !== 0 ? x.country : null });
      }

      const currencyInstance = this.gridAPI?.getFilterInstance('currency');

      if (currencyInstance) {
        currencyInstance.setModel({ values: x?.currency?.length !== 0 ? x.currency : null });
      }

      const investigatorInstance = this.gridAPI?.getFilterInstance('managed_by_name');

      if (investigatorInstance) {
        investigatorInstance.setModel({
          values: x?.managed_by_name?.length !== 0 ? x?.managed_by_name : null,
        });
      }

      this.gridAPI?.onFilterChanged();
    });

    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(() => this.addSite(), this.siteCtx.disabledAddSiteUI);
    });
  }

  castItemsToVendorOptions(items: unknown): OrganizationModel[] {
    return Array.isArray(items) ? items : [];
  }

  constructor() {
    this.patientProtocolService.get().pipe(takeUntilDestroyed()).subscribe();

    this.mainQuery
      .select('trialKey')
      .pipe(switchMap(() => this.patientProtocolService.getPatientProtocolVersions()))
      .pipe(tap(() => this.isProtocolVersionLoading$.next(true)))
      .pipe(takeUntilDestroyed())
      .subscribe((versions) => {
        setTimeout(() => {
          this.isProtocolVersionLoading$.next(false);
          this.protocolVersionOptions = versions.map(({ id, name }) => ({
            label: name,
            value: id,
          }));
          this.setDefaultProtocolFormValues();
        }, 0);
      });
  }

  getBulkUploadOption(): AuxButtonGroup {
    return {
      disabled: !this.permissions().userHasUpdateSiteCostsPermission,
      disabledTooltip: MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
      name: 'Add Site Invoiceables in Bulk',
      onClick: () => {
        this.overlayService.openPopup<unknown, undefined, BulkUploadSiteInvoiceablesModalComponent>(
          {
            content: BulkUploadSiteInvoiceablesModalComponent,
            settings: {
              header: 'Upload Site Invoiceables Bulk',
              panelClasses: ['min-w-[569px]'],
              primaryButton: {
                action: async (instance) => {
                  await instance?.onUpload();
                },
                label: 'Upload',
              },
            },
          }
        );
      },
    };
  }

  getSiteFlags(site: SiteModel) {
    const siteFlags: string[] = [];

    if (site.has_edc_data && !site.site_activation) {
      siteFlags.push('Activity detected for this site, please add an activation date');
    }

    if (site?.site_cost_groups_without_costs?.length) {
      const missingCostsGroups = site.site_cost_groups_without_costs.map((group) => {
        return `Missing costs for active patients: Site Budget Version: ${group.site_budget_version_name}, Patient Group: ${group.patient_group_name}`;
      });

      siteFlags.push(...missingCostsGroups);
    }

    return siteFlags;
  }

  gridDataReady() {
    const gridData: SiteGridData[] = [];
    const countries: FilterConfig[] = [];
    const currencies: FilterConfig[] = [];

    this.currentlyActiveSitesTotal = 0;
    this.activatedSitesTotal = 0;
    this.closedSitesTotal = 0;
    this.sitesQuery.getAll().forEach((site) => {
      this.resetFilter();
      const investigator_name = `${site.investigator?.given_name || ''} ${
        site.investigator?.family_name || ''
      }`;

      if (!countries.find((x) => x.name === Utils.countries[site?.country?.slice(8) || ''])) {
        countries.push({
          id: 'country',
          name: Utils.countries[site?.country?.slice(8) || ''],
        });
      }

      if (!currencies.find((x) => x.name === site.currency)) {
        currencies.push({
          id: 'currency',
          name: site.currency || '',
        });
      }

      if (site.site_activation && dayjs().isAfter(dayjs(site.site_activation))) {
        if (!site.closeout_date || dayjs().isBefore(site.closeout_date)) {
          this.currentlyActiveSitesTotal++;
        }

        this.activatedSitesTotal++;
      }

      if (site.closeout_date && dayjs().isAfter(dayjs(site.closeout_date))) {
        this.closedSitesTotal++;
      }

      gridData.push({
        closeout_date: site.closeout_date,
        country: site.country,
        currency: site.currency,
        id: site.id,
        investigator_family_name: site.investigator?.family_name,
        investigator_name,
        managed_by: site.managed_by_id,
        managed_by_name: this.organizationQuery.getEntity(site.managed_by_id)?.name,
        name: site.name,
        site_activation: site.site_activation,
        site_number: site.site_no,
        siteFlags: this.getSiteFlags(site),
        state: site.state,
        target_patients: site.target_patients,
      });
    });
    this.countryOptions$.next(countries.sort((x, y) => Utils.alphaNumSort(x.name, y.name)));
    this.currencyOptions$.next(currencies.sort((x, y) => Utils.alphaNumSort(x.name, y.name)));
    this.gridData$.next(gridData);
  }

  navigateToSiteInfoPage(event: CellClickedEvent) {
    const siteById = this.sitesQuery.getEntity(event.data.id);

    if (!siteById) return;

    void this.router.navigate([
      `${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${siteById.id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS}`,
    ]);
  }

  navigateToVendorPage(event: CellClickedEvent) {
    const org = this.organizationQuery.getEntity(event.data.managed_by);

    if (!org) return;

    void this.router.navigate([`${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.VENDORS}`], {
      queryParams: {
        selectedVendor: org.id,
      },
    });
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.gridAPI$.next(api);
    const allColumnIds: string[] = [];

    this.gridAPI?.getColumns()?.forEach((column: Column) => {
      allColumnIds.push(column.getColId());
    });
    this.gridAPI.autoSizeColumns(allColumnIds, false);
    this.gridAPI?.sizeColumnsToFit();
  }

  resetFilter() {
    this.transactionsForm.reset();
    this.gridAPI?.onFilterChanged();
  }

  private setDefaultProtocolFormValues() {
    if (this.protocolForm) {
      this.protocolForm.setValue({
        protocolVersion: this.protocolVersionOptions[0]?.value || null,
      });
    }
  }
}
